import React from 'react'
import { CSVImportWizard, FieldToMap } from './base'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { IProduct } from '@query/invoices'
import { createImportJob, createImportJobDryRun, ImportJobType } from '@query/importJob.ts'
import { useBusinessContext } from '@context'

interface Props {
  onComplete: () => void
}

const parseDecimal = (_: unknown, v: string) =>
  v === '' || v === undefined ? undefined : parseFloat(String(v).replace(',', '.'))

const parseOptionalInt = (_: unknown, v: string) => {
  if (v === '' || v === undefined) return undefined
  const n = parseInt(String(v), 10)
  return Number.isNaN(n) ? undefined : n
}

const parseBool = (_: unknown, v: string) => {
  if (v === '' || v === undefined) return false
  const s = String(v).toLowerCase()
  return s === 'true' || s === '1' || s === 'yes'
}

export const ProductImportWizard: React.FC<Props> = ({ onComplete }) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()

  const requiredField = yup.string().required(t('validation.required'))

  const fields: FieldToMap<keyof IProduct>[] = [
    {
      name: t('contacts.form.id'),
      info: t('products.import.idInfo'),
      csvField: 'ID',
      apiField: 'id',
      required: false,
      isIdentifier: true,
      fieldValidator: yup.string().optional(),
      valueValidator: yup
        .number()
        .nullable()
        .transform((_, value) => (value === '' ? null : Number(value))),
      hiddenByDefault: true
    },
    {
      name: t('products.form.code'),
      info: t('products.form.codeInfo'),
      csvField: 'Product Code',
      apiField: 'code',
      required: false,
      isIdentifier: true,
      fieldValidator: yup.string().optional(),
      valueValidator: yup.string().optional()
    },
    {
      name: t('products.form.name'),
      csvField: 'Name',
      apiField: 'name',
      required: true,
      fieldValidator: requiredField,
      valueValidator: yup.string().optional()
    },
    {
      name: t('products.form.unit'),
      info: t('products.form.unitInfo'),
      csvField: 'Unit',
      apiField: 'unit',
      required: true,
      fieldValidator: requiredField,
      valueValidator: yup.string().optional()
    },
    {
      name: t('products.form.unitAmount'),
      info: t('products.form.unitAmountInfo'),
      csvField: 'Amount',
      apiField: 'amount',
      required: true,
      fieldValidator: requiredField,
      valueValidator: yup.number().transform(parseDecimal).required()
    },
    {
      name: t('products.form.vatCode'),
      info: t('products.form.vatCodeInfo'),
      csvField: 'VAT Code',
      apiField: 'vat_code',
      required: true,
      fieldValidator: requiredField,
      valueValidator: yup.number().transform(parseOptionalInt).required()
    },
    {
      name: t('products.form.vatRate'),
      info: t('products.form.vatRateInfo'),
      csvField: 'VAT Rate',
      apiField: 'vat_rate',
      required: false,
      fieldValidator: yup.string().optional(),
      valueValidator: yup.number().transform(parseDecimal).optional()
    },
    {
      name: t('products.form.isVatInclusive'),
      csvField: 'VAT Inclusive',
      apiField: 'is_vat_inclusive',
      required: false,
      fieldValidator: yup.string().optional(),
      valueValidator: yup.boolean().transform(parseBool).optional()
    },
    {
      name: t('products.form.account'),
      info: t('products.form.accountInfo'),
      csvField: 'Account ID',
      apiField: 'account_id',
      required: false,
      fieldValidator: yup.string().optional(),
      valueValidator: yup
        .number()
        .nullable()
        .transform((_, value) => (value === '' ? null : Number(value)))
        .optional()
    },
    {
      name: t('products.form.description'),
      info: t('products.form.descriptionInfo'),
      csvField: 'Description',
      apiField: 'description',
      required: false,
      fieldValidator: yup.string().optional(),
      valueValidator: yup.string().optional()
    }
  ]

  return (
    <CSVImportWizard
      fields={fields}
      reviewImportDescriptionKey="components.importWizard.review.importInfoProducts"
      onVerifyData={async data => {
        const { to_be_created: toBeCreated, to_be_updated: toBeUpdated } =
          await createImportJobDryRun({ slug }, { type: ImportJobType.PRODUCTS, data })
        return { toBeCreated, toBeUpdated }
      }}
      onImportData={async data => {
        const result = await createImportJob({ slug }, { type: ImportJobType.PRODUCTS, data })
        onComplete()
        return result
      }}
    />
  )
}
